import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
} from "d3-force";
import type {
  Simulation,
  SimulationLinkDatum,
  SimulationNodeDatum,
} from "d3-force";
import { useEffect, useMemo, useRef } from "react";

export interface GraphNode {
  id: string;
  type: "term" | "doha";
  slug: string;
  label: string;
}

export interface GraphEdge {
  source: string;
  target: string;
  kind: "term-term" | "doha-term";
}

export interface GlossaryGraphProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  focusId?: string | null;
  onSelectNode?: (node: GraphNode) => void;
  className?: string;
}

// Internal simulation node: a copy of a GraphNode with mutable layout fields.
interface SimNode extends SimulationNodeDatum {
  id: string;
  type: "term" | "doha";
  slug: string;
  label: string;
}

type SimEdge = SimulationLinkDatum<SimNode> & { kind: GraphEdge["kind"] };

const LABEL_FONT = '500 12px "Alegreya", Georgia, serif';

// Colours come from the theme's CSS variables so the graph adapts to
// light / dark automatically (term = soft ink, doha = terracotta,
// edges = hairline, labels = ink).
interface Palette {
  term: string;
  doha: string;
  edge: string;
  edgeFocus: string;
  label: string;
}

function readPalette(el: HTMLElement): Palette {
  const cs = getComputedStyle(el);
  const read = (name: string, fallback: string): string =>
    cs.getPropertyValue(name).trim() || fallback;
  return {
    term: read("--color-ink-soft", "#4a453d"),
    doha: read("--color-terracotta", "#c15f3c"),
    edge: read("--color-line", "rgba(31,29,26,0.12)"),
    edgeFocus: read("--color-terracotta", "#c15f3c"),
    label: read("--color-ink", "#1f1d1a"),
  };
}

const MIN_SCALE = 0.2;
const MAX_SCALE = 4;
const LABEL_SCALE_THRESHOLD = 1.4; // above this zoom, draw all labels
const CLICK_MOVE_TOLERANCE = 4; // px of pointer travel still counted as a click
const ALPHA_STOP = 0.02; // stop the RAF loop below this alpha

function radiusForType(type: GraphNode["type"]): number {
  return type === "doha" ? 8 : 5.5;
}

export default function GlossaryGraph(props: GlossaryGraphProps) {
  const { nodes, edges, focusId = null, onSelectNode, className } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Keep the latest onSelectNode without re-running the heavy effect.
  const onSelectRef = useRef<GlossaryGraphProps["onSelectNode"]>(onSelectNode);
  useEffect(() => {
    onSelectRef.current = onSelectNode;
  }, [onSelectNode]);

  // Stable copies of the sim data. Rebuilt only when node/edge identity changes
  // (by length — the parent is expected to pass new arrays on real changes).
  const simNodes = useMemo<SimNode[]>(
    () =>
      nodes.map((n, i) => {
        // Seed initial positions on a small ring around the origin so layout is
        // deterministic-ish and never starts as a single overlapping dot.
        const angle = (i / Math.max(1, nodes.length)) * Math.PI * 2;
        const seedR = 40 + (i % 7) * 6;
        return {
          id: n.id,
          type: n.type,
          slug: n.slug,
          label: n.label,
          x: Math.cos(angle) * seedR,
          y: Math.sin(angle) * seedR,
        };
      }),
    [nodes],
  );

  const simEdges = useMemo<SimEdge[]>(
    () =>
      edges.map((e) => ({ source: e.source, target: e.target, kind: e.kind })),
    [edges],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (canvas === null || container === null) return;
    const ctxOrNull = canvas.getContext("2d");
    if (ctxOrNull === null) return;
    // Bind to a non-null-typed const so the nested draw closures keep the
    // narrowing (TS drops control-flow narrowing of `let`/nullable consts
    // inside nested functions).
    const ctx: CanvasRenderingContext2D = ctxOrNull;

    let disposed = false;
    let palette = readPalette(container);

    // ---- View transform (graph-space -> screen-space) --------------------
    // screenX = (graphX + tx) * scale ... where tx/ty are stored pre-scale so
    // that panning by screen pixels is `t += deltaScreen / scale`. We invert
    // with: graphX = screenX / scale - tx.
    let scale = 1;
    let tx = 0;
    let ty = 0;
    // Center the origin in the viewport initially.
    let viewInitialised = false;

    // ---- Interaction state ----------------------------------------------
    let hovered: SimNode | null = null;
    let draggingNode: SimNode | null = null;
    let panning = false;
    let pointerMoved = 0; // accumulated screen-space travel since pointerdown
    let downNode: SimNode | null = null;

    const focusNode =
      focusId === null
        ? null
        : (simNodes.find((n) => n.id === focusId) ?? null);

    // ---- Simulation ------------------------------------------------------
    // Scale the physics to the graph size: a 40-node ego-net wants to
    // breathe (strong charge, long links); a 2,500-node full web wants to
    // stay a legible mass (weak charge, short links, firmer centering).
    const n = simNodes.length;
    const chargeStrength = -Math.max(
      20,
      Math.min(200, 1450 / Math.sqrt(Math.max(1, n))),
    );
    const linkDistance = n > 1200 ? 16 : n > 300 ? 30 : 48;
    const centeringStrength = n > 1200 ? 0.08 : 0.04;
    const collidePad = n > 1200 ? 1 : 4;
    const decay = n > 1200 ? 0.045 : 0.028;

    const sim: Simulation<SimNode, SimEdge> = forceSimulation<SimNode>(simNodes)
      .force(
        "link",
        forceLink<SimNode, SimEdge>(simEdges)
          .id((d) => d.id)
          .distance(linkDistance)
          .strength(0.35),
      )
      .force("charge", forceManyBody<SimNode>().strength(chargeStrength))
      .force("center", forceCenter(0, 0))
      .force(
        "collide",
        forceCollide<SimNode>().radius(
          (d) => radiusForType(d.type) + collidePad,
        ),
      )
      .force("x", forceX<SimNode>(0).strength(centeringStrength))
      .force("y", forceY<SimNode>(0).strength(centeringStrength))
      .alphaDecay(decay)
      .stop();

    // ---- Canvas sizing (devicePixelRatio-aware) --------------------------
    let cssWidth = container.clientWidth;
    let cssHeight = container.clientHeight;

    function applyCanvasSize(): void {
      const c = canvas;
      if (c === null) return;
      const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
      cssWidth = container === null ? 0 : container.clientWidth;
      cssHeight = container === null ? 0 : container.clientHeight;
      c.width = Math.max(1, Math.round(cssWidth * dpr));
      c.height = Math.max(1, Math.round(cssHeight * dpr));
      c.style.width = `${cssWidth}px`;
      c.style.height = `${cssHeight}px`;
      // Draw in CSS pixels; the transform below maps to device pixels.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (!viewInitialised && cssWidth > 0 && cssHeight > 0) {
        // Place the graph origin at the viewport center.
        tx = cssWidth / 2 / scale;
        ty = cssHeight / 2 / scale;
        viewInitialised = true;
      }
    }
    applyCanvasSize();

    // ---- Rendering -------------------------------------------------------
    // Precompute an incidence set for the focus node so we can emphasise its edges.
    function endpointId(v: SimNode | string | number): string {
      if (typeof v === "string") return v;
      if (typeof v === "number") return String(v);
      return v.id;
    }

    function draw(): void {
      ctx.clearRect(0, 0, cssWidth, cssHeight);
      if (simNodes.length === 0) return;

      const showAllLabels = scale >= LABEL_SCALE_THRESHOLD;

      // Edges first.
      ctx.lineWidth = 1;
      for (const e of simEdges) {
        const s = e.source as SimNode;
        const t = e.target as SimNode;
        if (typeof s !== "object" || typeof t !== "object") continue;
        if (
          s.x === undefined ||
          s.y === undefined ||
          t.x === undefined ||
          t.y === undefined
        ) {
          continue;
        }
        const isFocusEdge =
          focusNode !== null &&
          (endpointId(s) === focusNode.id || endpointId(t) === focusNode.id);
        ctx.beginPath();
        ctx.moveTo((s.x + tx) * scale, (s.y + ty) * scale);
        ctx.lineTo((t.x + tx) * scale, (t.y + ty) * scale);
        if (isFocusEdge) {
          ctx.strokeStyle = palette.edgeFocus;
          ctx.lineWidth = 1.6;
          ctx.globalAlpha = 0.5;
        } else {
          ctx.strokeStyle = palette.edge;
          ctx.lineWidth = 1;
          ctx.globalAlpha = 1;
        }
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      // Nodes.
      for (const n of simNodes) {
        if (n.x === undefined || n.y === undefined) continue;
        const sx = (n.x + tx) * scale;
        const sy = (n.y + ty) * scale;
        const r = radiusForType(n.type);
        const isFocus = focusNode !== null && n.id === focusNode.id;
        const isHovered = hovered !== null && n.id === hovered.id;

        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        if (n.type === "doha") {
          ctx.globalAlpha = isFocus || isHovered ? 0.85 : 0.5;
          ctx.fillStyle = palette.doha;
        } else {
          ctx.globalAlpha = 1;
          ctx.fillStyle = palette.term;
        }
        ctx.fill();

        if (isFocus) {
          ctx.globalAlpha = 1;
          ctx.strokeStyle = palette.edgeFocus;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
        ctx.globalAlpha = 1;

        if (isFocus || isHovered || showAllLabels) {
          ctx.font = LABEL_FONT;
          ctx.fillStyle = palette.label;
          ctx.textBaseline = "middle";
          ctx.fillText(n.label, sx + r + 4, sy);
        }
      }
    }

    // ---- Animation loop --------------------------------------------------
    let rafId = 0;
    let running = false;

    function tick(): void {
      if (disposed) return;
      sim.tick();
      draw();
      if (sim.alpha() > ALPHA_STOP) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        running = false;
      }
    }

    function ensureRunning(): void {
      if (running || disposed) return;
      running = true;
      rafId = window.requestAnimationFrame(tick);
    }

    // Kick off the initial layout warm.
    sim.alpha(0.9).restart();
    ensureRunning();

    // ---- Hit testing -----------------------------------------------------
    // Convert a screen (client) coordinate to graph-space, then find the
    // topmost node whose radius contains it. Radius is in graph units but the
    // click tolerance grows as we zoom out (smaller on screen), so we test in
    // screen space against the on-screen radius.
    function nodeAt(clientX: number, clientY: number): SimNode | null {
      const rect = canvas === null ? null : canvas.getBoundingClientRect();
      if (rect === null) return null;
      const screenX = clientX - rect.left;
      const screenY = clientY - rect.top;
      // Iterate in reverse so later-drawn (on top) nodes win.
      for (let i = simNodes.length - 1; i >= 0; i--) {
        const n = simNodes[i];
        if (n === undefined || n.x === undefined || n.y === undefined) continue;
        const sx = (n.x + tx) * scale;
        const sy = (n.y + ty) * scale;
        const hitR = radiusForType(n.type) + 3;
        const dx = screenX - sx;
        const dy = screenY - sy;
        if (dx * dx + dy * dy <= hitR * hitR) return n;
      }
      return null;
    }

    // ---- Pointer + wheel handlers ---------------------------------------
    function onPointerDown(ev: PointerEvent): void {
      const c = canvas;
      if (c === null) return;
      c.setPointerCapture(ev.pointerId);
      pointerMoved = 0;
      const hit = nodeAt(ev.clientX, ev.clientY);
      downNode = hit;
      if (hit !== null) {
        draggingNode = hit;
        // Pin the node during drag so the simulation follows the pointer.
        hit.fx = hit.x;
        hit.fy = hit.y;
        sim.alphaTarget(0.15).restart();
        ensureRunning();
      } else {
        panning = true;
      }
    }

    function onPointerMove(ev: PointerEvent): void {
      const rect = canvas === null ? null : canvas.getBoundingClientRect();
      if (rect === null) return;
      if (draggingNode !== null) {
        pointerMoved += Math.abs(ev.movementX) + Math.abs(ev.movementY);
        // Map screen delta into graph-space: graph = screen/scale - t.
        const gx = (ev.clientX - rect.left) / scale - tx;
        const gy = (ev.clientY - rect.top) / scale - ty;
        draggingNode.fx = gx;
        draggingNode.fy = gy;
        ensureRunning();
        return;
      }
      if (panning) {
        pointerMoved += Math.abs(ev.movementX) + Math.abs(ev.movementY);
        // Panning shifts the pre-scale translation by screen delta / scale.
        tx += ev.movementX / scale;
        ty += ev.movementY / scale;
        if (!running) draw();
        return;
      }
      // Plain hover.
      const hit = nodeAt(ev.clientX, ev.clientY);
      if (hit !== hovered) {
        hovered = hit;
        if (canvas !== null)
          canvas.style.cursor = hit === null ? "default" : "pointer";
        if (!running) draw();
      }
    }

    function onPointerUp(ev: PointerEvent): void {
      const c = canvas;
      if (c !== null && c.hasPointerCapture(ev.pointerId)) {
        c.releasePointerCapture(ev.pointerId);
      }
      const wasClick = pointerMoved <= CLICK_MOVE_TOLERANCE;

      if (draggingNode !== null) {
        // Release the pin so the node relaxes back into the layout.
        draggingNode.fx = null;
        draggingNode.fy = null;
        draggingNode = null;
        sim.alphaTarget(0).alpha(Math.max(sim.alpha(), 0.2)).restart();
        ensureRunning();
      }
      panning = false;

      if (wasClick && downNode !== null) {
        const original = nodes.find((n) => n.id === downNode?.id) ?? null;
        if (original !== null && onSelectRef.current !== undefined) {
          onSelectRef.current(original);
        }
      }
      downNode = null;
    }

    function onWheel(ev: WheelEvent): void {
      ev.preventDefault();
      const rect = canvas === null ? null : canvas.getBoundingClientRect();
      if (rect === null) return;
      const screenX = ev.clientX - rect.left;
      const screenY = ev.clientY - rect.top;
      // Graph point currently under the cursor (invariant across the zoom).
      const gx = screenX / scale - tx;
      const gy = screenY / scale - ty;
      const factor = Math.exp(-ev.deltaY * 0.0015);
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale * factor));
      scale = next;
      // Re-solve translation so (gx,gy) stays under the cursor:
      // screen = (g + t) * scale  =>  t = screen/scale - g.
      tx = screenX / scale - gx;
      ty = screenY / scale - gy;
      if (!running) draw();
    }

    const canvasEl = canvas;
    canvasEl.addEventListener("pointerdown", onPointerDown);
    canvasEl.addEventListener("pointermove", onPointerMove);
    canvasEl.addEventListener("pointerup", onPointerUp);
    canvasEl.addEventListener("wheel", onWheel, { passive: false });

    // ---- Resize ----------------------------------------------------------
    const resizeObserver = new ResizeObserver(() => {
      applyCanvasSize();
      if (!running) draw();
    });
    resizeObserver.observe(container);

    // ---- Theme change (light / dark) -------------------------------------
    const darkMedia = window.matchMedia("(prefers-color-scheme: dark)");
    const onThemeChange = (): void => {
      palette = readPalette(container);
      if (!running) draw();
    };
    darkMedia.addEventListener("change", onThemeChange);

    // ---- Cleanup ---------------------------------------------------------
    return () => {
      disposed = true;
      window.cancelAnimationFrame(rafId);
      sim.stop();
      resizeObserver.disconnect();
      darkMedia.removeEventListener("change", onThemeChange);
      canvasEl.removeEventListener("pointerdown", onPointerDown);
      canvasEl.removeEventListener("pointermove", onPointerMove);
      canvasEl.removeEventListener("pointerup", onPointerUp);
      canvasEl.removeEventListener("wheel", onWheel);
    };
    // Re-run (and reheat) when the graph data or focus changes.
  }, [simNodes, simEdges, focusId, nodes]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          touchAction: "none",
        }}
      />
    </div>
  );
}
